<template>
<!-- Panel: a toolbar of the user's queues. Hidden and inert while minimized -->
  <Transition name="task-panel-fade">
    <div v-if="shouldShowPanel" v-show="!isMinimized" ref="panelRef" role="toolbar" aria-label="Work queues"
      :aria-orientation="isVertical ? 'vertical' : 'horizontal'" :inert="isMinimized"
      class="task-panel task-panel-layer fixed isolate flex items-center gap-0.5 p-1 rounded-2xl border shadow-2xl dark:shadow-black/60 no-print"
      :class="[isVertical ? 'flex-col is-vertical' : 'flex-row', { 'is-on-topbar': panelOnTopBar, 'is-dragging': isDragging }]"
      :style="{ left: position.x + 'px', top: panelTop + 'px' }">
    <!-- Surface -->
      <div aria-hidden="true" class="task-panel-surface absolute inset-0 -z-10 rounded-2xl pointer-events-none"></div>
    <!-- Grip -->
      <Popper hover arrow :placement="tipPlacement" content="Drag to move">
        <button type="button" class="bn-icon-only task-panel-grip" aria-label="Move queue panel. Arrow keys move it, Home resets it."
          @pointerdown="startPanelDrag" @dblclick="resetPanelPosition" @keydown="onGripKeydown"><DotsVerticalIcon /></button>
      </Popper>
    <!-- Queues. Vertical: Popper's 12px transparent hover border counts in its width, so it takes 100% + 24px. Only the
         trigger stretches: the wrapper also holds the .popper div, which takes its label's width rather than wrapping
         inside the narrow panel. -->
      <Popper v-for="queue in visibleQueues" :key="queue.key" hover arrow :placement="tipPlacement" :content="queue.label"
        :class="{ 'w-[calc(100%+24px)] [&>div:not(.popper)]:w-full [&>.popper]:w-max': isVertical }">
        <router-link :to="queue.path" replace class="task-panel-item" :aria-label="queueLabel(queue)"
          :aria-current="isCurrent(queue) ? 'page' : null">
          <component :is="queue.component" @count="(n) => counts[queue.key] = n" />
        </router-link>
      </Popper>
      <div class="task-panel-rule" aria-hidden="true"></div>
    <!-- Layout and minimize -->
      <Popper hover arrow :placement="tipPlacement" :content="isVertical ? 'Horizontal layout' : 'Vertical layout'">
        <button type="button" class="bn-icon-only" :aria-label="isVertical ? 'Horizontal layout' : 'Vertical layout'" @click="toggleLayout">
          <SwitchHorizontalIcon v-if="isVertical" /><SwitchVerticalIcon v-else />
        </button>
      </Popper>
      <Popper hover arrow :placement="tipPlacement" content="Minimize">
        <button ref="minimizeRef" type="button" class="bn-icon-only" aria-label="Minimize queue panel" aria-expanded="true" @click="minimize"><MinusSmIcon /></button>
      </Popper>
    </div>
  </Transition>

<!-- Minimized pill: the total open count; click restores, drag moves -->
  <Transition name="task-panel-fade">
    <div v-if="shouldShowPanel" v-show="isMinimized" ref="pillRef"
      class="task-panel-layer fixed isolate rounded-full border shadow-2xl dark:shadow-black/60 no-print"
      :class="{ 'is-on-topbar': pillOnTopBar }" :style="pillStyle">
      <div aria-hidden="true" class="task-panel-pill-surface absolute inset-0 -z-10 rounded-full pointer-events-none"></div>
      <Popper hover arrow placement="top" content="Show panel">
        <button ref="pillButtonRef" type="button" class="task-panel-pill" :class="{ 'is-dragging': pillDragging }"
          :aria-label="`Show queue panel, ${totalOpen} open. Drag or use the arrow keys to move it.`" aria-expanded="false"
          @pointerdown="startPillDrag" @click="onPillClick" @keydown="onPillKeydown">
          <InboxIcon /><span>Queues</span><span v-if="totalOpen > 0" class="bn-count text-amber">{{ totalOpen }}</span>
        </button>
      </Popper>
    </div>
  </Transition>
</template>

<script>
import { ref, reactive, computed, watch, inject, nextTick, onMounted, onUnmounted } from 'vue'
import { useRoute } from 'vue-router'
import { DotsVerticalIcon, MinusSmIcon, InboxIcon } from '@heroicons/vue/solid'
import { SwitchVerticalIcon, SwitchHorizontalIcon } from '@heroicons/vue/outline'
import WarehouseQueue from "@/components/warehouse/WarehouseQueue.vue"
import RackBuildQueue from '@/components/warehouse/rackBuilds/RackBuildQueue.vue';
import InvoiceQueue from "@/components/invoices/InvoiceQueue.vue"
import IntegratorFormQueue from "@/components/companies/IntegratorFormQueue.vue"
import VendorInvoiceQueue from "@/components/vendorInvoices/VendorInvoiceQueue.vue"

const TOP_BAR_HEIGHT = 48        // h-12 top bar
const SIGN_OUT_CLEAR = 83        // top bar + planned sign-out line (29px) + a 6px gap
const DRAG_THRESHOLD = 4         // px a pill press must move before it counts as a drag
const OLD_STORAGE_KEY = 'taskPanelPreferences'

const getDefaultPosition = () => {
  const width = window.innerWidth
  // Responsive defaults based on screen size
  if (width < 768) { // Mobile
    return { x: 10, y: 54 }
  } else if (width < 1024) { // Tablet
    return { x: 20, y: 80 }
  } else { // Desktop
    return { x: 10, y: 54 }
  }
}

export default {
  components: {
    DotsVerticalIcon,
    MinusSmIcon,
    InboxIcon,
    SwitchVerticalIcon,
    SwitchHorizontalIcon
  },
  setup() {
    const global = inject('global')
    const {authenticated, plannedSignOut} = global
    const route = useRoute()

  // Refs
    const panelRef = ref(null)
    const pillRef = ref(null)
    const pillButtonRef = ref(null)
    const minimizeRef = ref(null)
    const position = ref(getDefaultPosition())
    const pillPosition = ref(null)
    const isMinimized = ref(false)
    const isVertical = ref(false)
    const isDragging = ref(false)
    const pillDragging = ref(false)
    const panelSize = ref({ w: 0, h: 0 })
    const pillSize = ref({ w: 0, h: 0 })
    const lastWindowSize = ref({ width: window.innerWidth, height: window.innerHeight })
    const counts = reactive({})
    let preferencesLoaded = false
    // The edges a resize holds the panel to ({ right, bottom }), read on the first resize after it was last placed
    let anchor = null
    let dragOffset = { x: 0, y: 0 }
    let pillStart = { x: 0, y: 0 }
    let pillOffset = { x: 0, y: 0 }
    let pillMoved = false
    let saveTimeout = null
    let sizeObserver = null

  // Computed
    const isPrimary = computed(()=>{
      return authenticated.value?.loggedIn && authenticated.value?.organization?.orgType?.includes('primary')
    })

    const allowInvoices = computed(()=>{
      return isPrimary.value && (authenticated.value?.role?.accessModel?.invoices ?? 0) >= 1
    })

    const allowVendorInvoices = computed(()=>{
      const accessModel = authenticated.value?.role?.accessModel
      return isPrimary.value && ((accessModel?.projects ?? 0) >= 4 || (accessModel?.payroll ?? 0) >= 1)
    })

    const allowRackBuilds = computed(()=>{
      return isPrimary.value && (authenticated.value?.role?.accessModel?.rackQueue ?? 0) >= 1
    })

    const allowWarehouse = computed(()=>{
      return isPrimary.value && (authenticated.value?.role?.accessModel?.warehouseQueue ?? 0) >= 1
    })

    const allowIntegratorForms = computed(()=>{
      return isPrimary.value && (authenticated.value?.role?.accessModel?.organizations ?? 0) >= 3
    })

    const queues = computed(()=>{
      // Only show queues if user is logged in and has proper authentication
      if (!authenticated.value?.loggedIn) return []

      return [
        {key:'invoices',label:'Invoices',component:InvoiceQueue,path:'/invoiceRequests',condition:allowInvoices.value},
        {key:'vendorInvoices',label:'Vendor Invoices',component:VendorInvoiceQueue,path:'/vendorInvoices',condition:allowVendorInvoices.value},
        {key:'rackBuilds',label:'Rack Builds',component:RackBuildQueue,path:'/rackBuildRequests',condition:allowRackBuilds.value},
        {key:'warehouse',label:'Warehouse Requests',component:WarehouseQueue,path:'/shippingRequests',condition:allowWarehouse.value},
        {key:'integratorForms',label:'Integrator Forms',component:IntegratorFormQueue,path:'/integratorInformationForms',condition:allowIntegratorForms.value}
      ]
    })

    const visibleQueues = computed(() => queues.value.filter(queue => queue.condition))

    const hasVisibleQueues = computed(() => {
      return queues.value.some(queue => queue.condition)
    })

    const shouldShowPanel = computed(() => {
      return hasVisibleQueues.value
    })

    const totalOpen = computed(() => visibleQueues.value.reduce((sum, queue) => sum + (counts[queue.key] || 0), 0))

    const tipPlacement = computed(() => isVertical.value ? 'right' : 'bottom')

    const panelOnTopBar = computed(() => position.value.y + panelSize.value.h / 2 < TOP_BAR_HEIGHT)

    const pillOnTopBar = computed(() => !!pillPosition.value && pillPosition.value.y + pillSize.value.h / 2 < TOP_BAR_HEIGHT)

    // While the planned sign-out bar shows, a panel in the content area draws below it; one parked on the bar stays put.
    // The saved position is unchanged.
    const panelTop = computed(() => plannedSignOut.value && !panelOnTopBar.value ? Math.max(position.value.y, SIGN_OUT_CLEAR) : position.value.y)

    const pillTop = computed(() => {
      if (!pillPosition.value) return null
      return plannedSignOut.value && !pillOnTopBar.value ? Math.max(pillPosition.value.y, SIGN_OUT_CLEAR) : pillPosition.value.y
    })

    const pillStyle = computed(() => pillPosition.value
      ? { left: pillPosition.value.x + 'px', top: pillTop.value + 'px' }
      : { left: '8px', bottom: '8px' })

  // Helpers
    const storageKey = () => {
      const id = authenticated.value?._id
      return id ? `taskPanelPreferences:${id}` : null
    }

    const validatePosition = (pos) => {
      // Ensure position is an object with numeric x and y
      if (!pos || typeof pos !== 'object' || !Object.prototype.hasOwnProperty.call(pos, 'x') || !Object.prototype.hasOwnProperty.call(pos, 'y')) {
        return false
      }

      const x = Number(pos.x)
      const y = Number(pos.y)

      // Check if coordinates are valid numbers
      if (isNaN(x) || isNaN(y)) {
        return false
      }

      // Check if coordinates are within reasonable bounds
      if (x < 0 || y < 0 || x > 10000 || y > 10000) {
        return false
      }

      return true
    }

    const validateMinimized = (minimized) => {
      return typeof minimized === 'boolean'
    }

    const validateVertical = (vertical) => {
      return typeof vertical === 'boolean'
    }

    // Per-user settings. The first user to sign in after the move takes over the old shared setting, which is then removed.
    const loadPreferences = () => {
      const key = storageKey()
      if (!key) return
      preferencesLoaded = true
      try {
        let stored = localStorage.getItem(key)
        if (!stored) {
          const old = localStorage.getItem(OLD_STORAGE_KEY)
          if (old) {
            localStorage.setItem(key, old)
            localStorage.removeItem(OLD_STORAGE_KEY)
            stored = old
          }
        }
        if (!stored) return

        const prefs = JSON.parse(stored)

        if (validatePosition(prefs?.position)) {
          position.value = { x: Number(prefs.position.x), y: Number(prefs.position.y) }
          anchor = null
        }
        if (prefs?.pillPosition === null || validatePosition(prefs?.pillPosition)) {
          pillPosition.value = prefs.pillPosition ? { x: Number(prefs.pillPosition.x), y: Number(prefs.pillPosition.y) } : null
        }
        if (validateMinimized(prefs?.minimized)) {
          isMinimized.value = prefs.minimized
        }
        if (validateVertical(prefs?.isVertical)) {
          isVertical.value = prefs.isVertical
        }
      } catch (error) {
        console.warn('Failed to load taskPanel preferences from localStorage:', error)
        // Clear corrupted data
        try { localStorage.removeItem(key) } catch (e) { /* storage unavailable */ }
      }
    }

    const savePreferences = () => {
      const key = storageKey()
      if (!key || !shouldShowPanel.value) return
      try {
        const prefs = {
          position: position.value,
          pillPosition: pillPosition.value,
          minimized: isMinimized.value,
          isVertical: isVertical.value
        }
        localStorage.setItem(key, JSON.stringify(prefs))
      } catch (error) {
        console.warn('Failed to save taskPanel preferences to localStorage:', error)
      }
    }

    // Debounced save function
    const debouncedSave = () => {
      if (saveTimeout) clearTimeout(saveTimeout)
      saveTimeout = setTimeout(savePreferences, 300)
    }

    // The minimum is 0, not the top bar's height, so the panel and pill can sit on the top bar
    const clampToViewport = (pos, size) => {
      const maxX = Math.max(0, window.innerWidth - (size?.w ?? 0))
      const maxY = Math.max(0, window.innerHeight - (size?.h ?? 0))
      return {
        x: Math.max(0, Math.min(pos.x, maxX)),
        y: Math.max(0, Math.min(pos.y, maxY))
      }
    }

    const clampPanel = () => {
      position.value = clampToViewport(position.value, panelSize.value)
    }

    const clampPill = () => {
      if (pillPosition.value) pillPosition.value = clampToViewport(pillPosition.value, pillSize.value)
    }

    // A resize keeps the panel's distance from the edges it was nearer when last placed (dragged, moved, reset or
    // loaded), never rereading them mid-resize: a panel beside the top bar's right-hand buttons stays clear of them, one
    // on the bar stays on it, and narrowing then widening the window brings it back where it was.
    const placedAnchor = () => ({
      right: position.value.x > lastWindowSize.value.width - position.value.x - panelSize.value.w,
      bottom: position.value.y > lastWindowSize.value.height - position.value.y - panelSize.value.h
    })

    const keepFromEdge = (start, size, oldSpan, newSpan, fromEnd) => fromEnd ? newSpan - size - (oldSpan - start - size) : start

    // offsetWidth/offsetHeight, not the bounding rect, so the fade's scale doesn't skew the size. A hidden element reads 0×0.
    const observeSizes = () => {
      sizeObserver?.disconnect()
      if (typeof ResizeObserver === 'undefined') return
      sizeObserver = new ResizeObserver((entries) => {
        entries.forEach((entry) => {
          const w = entry.target.offsetWidth
          const h = entry.target.offsetHeight
          if (!w && !h) return
          if (entry.target === panelRef.value) {
            panelSize.value = { w, h }
            clampPanel()
          } else if (entry.target === pillRef.value) {
            pillSize.value = { w, h }
            clampPill()
          }
        })
      })
      if (panelRef.value) sizeObserver.observe(panelRef.value)
      if (pillRef.value) sizeObserver.observe(pillRef.value)
    }

    const arrowDelta = (e) => {
      const step = e.shiftKey ? 50 : 10
      return {
        ArrowLeft: { x: -step, y: 0 },
        ArrowRight: { x: step, y: 0 },
        ArrowUp: { x: 0, y: -step },
        ArrowDown: { x: 0, y: step }
      }[e.key] ?? null
    }

    const queueLabel = (queue) => {
      const n = counts[queue.key] || 0
      return n > 0 ? `${queue.label}, ${n} open` : `${queue.label}, none open`
    }

    const isCurrent = (queue) => route.path === queue.path

  // Watchers
    watch([panelRef, pillRef], observeSizes)

    // Queues that appear later (a role change while signed in) open in the saved state
    watch(shouldShowPanel, (show) => {
      if (show && !preferencesLoaded) loadPreferences()
    })

  // Lifecycle
    // Read before the first draw, so the panel opens in its saved state with no shrink
    if (shouldShowPanel.value) loadPreferences()

    onMounted(() => {
      window.addEventListener('resize', handleResize)
      observeSizes()
      nextTick(() => {
        clampPanel()
        clampPill()
      })
    })

    onUnmounted(() => {
      window.removeEventListener('resize', handleResize)
      sizeObserver?.disconnect()
      if (saveTimeout) clearTimeout(saveTimeout)
    })

  // Handlers
    const onPanelDrag = (e) => {
      if (!isDragging.value) return
      position.value = clampToViewport({ x: e.clientX - dragOffset.x, y: e.clientY - dragOffset.y }, panelSize.value)
      anchor = null
    }

    const endPanelDrag = (e) => {
      isDragging.value = false
      e.currentTarget.removeEventListener('pointermove', onPanelDrag)
      e.currentTarget.removeEventListener('pointerup', endPanelDrag)
      e.currentTarget.removeEventListener('pointercancel', endPanelDrag)
      debouncedSave()
    }

    const startPanelDrag = (e) => {
      if (e.button !== 0 || !panelRef.value) return
      e.preventDefault()
      const grip = e.currentTarget
      grip.setPointerCapture(e.pointerId)
      const rect = panelRef.value.getBoundingClientRect()
      dragOffset = { x: e.clientX - rect.left, y: e.clientY - rect.top }
      isDragging.value = true
      grip.addEventListener('pointermove', onPanelDrag)
      grip.addEventListener('pointerup', endPanelDrag)
      grip.addEventListener('pointercancel', endPanelDrag)
    }

    const resetPanelPosition = () => {
      position.value = clampToViewport(getDefaultPosition(), panelSize.value)
      anchor = null
      savePreferences()
    }

    const onGripKeydown = (e) => {
      if (e.key === 'Home') {
        e.preventDefault()
        resetPanelPosition()
        return
      }
      const delta = arrowDelta(e)
      if (!delta) return
      e.preventDefault()
      position.value = clampToViewport({ x: position.value.x + delta.x, y: position.value.y + delta.y }, panelSize.value)
      anchor = null
      debouncedSave()
    }

    const onPillDrag = (e) => {
      if (!pillMoved) {
        if (Math.hypot(e.clientX - pillStart.x, e.clientY - pillStart.y) < DRAG_THRESHOLD) return
        pillMoved = true
        pillDragging.value = true
      }
      pillPosition.value = clampToViewport({ x: e.clientX - pillOffset.x, y: e.clientY - pillOffset.y }, pillSize.value)
    }

    const endPillDrag = (e) => {
      pillDragging.value = false
      e.currentTarget.removeEventListener('pointermove', onPillDrag)
      e.currentTarget.removeEventListener('pointerup', endPillDrag)
      e.currentTarget.removeEventListener('pointercancel', endPillDrag)
      if (pillMoved) debouncedSave()
    }

    const startPillDrag = (e) => {
      if (e.button !== 0 || !pillRef.value) return
      // Stops text being selected during a drag
      e.preventDefault()
      const button = e.currentTarget
      button.setPointerCapture(e.pointerId)
      const rect = pillRef.value.getBoundingClientRect()
      pillStart = { x: e.clientX, y: e.clientY }
      pillOffset = { x: e.clientX - rect.left, y: e.clientY - rect.top }
      pillMoved = false
      button.addEventListener('pointermove', onPillDrag)
      button.addEventListener('pointerup', endPillDrag)
      button.addEventListener('pointercancel', endPillDrag)
    }

    const minimize = () => {
      isMinimized.value = true
      savePreferences()
      nextTick(() => pillButtonRef.value?.focus())
    }

    const restore = () => {
      isMinimized.value = false
      savePreferences()
      // The window may have changed while minimized
      nextTick(() => {
        clampPanel()
        minimizeRef.value?.focus()
      })
    }

    const onPillClick = () => {
      if (pillMoved) {
        pillMoved = false
        return
      }
      restore()
    }

    const onPillKeydown = (e) => {
      if (e.key === 'Home') {
        e.preventDefault()
        pillPosition.value = null
        savePreferences()
        return
      }
      const delta = arrowDelta(e)
      if (!delta) return
      e.preventDefault()
      const rect = pillRef.value?.getBoundingClientRect()
      const from = pillPosition.value ?? { x: rect?.left ?? 0, y: rect?.top ?? 0 }
      pillPosition.value = clampToViewport({ x: from.x + delta.x, y: from.y + delta.y }, pillSize.value)
      debouncedSave()
    }

    const handleResize = () => {
      const currentWidth = window.innerWidth
      const currentHeight = window.innerHeight
      const { w, h } = panelSize.value
      if (!anchor) anchor = placedAnchor()
      const x = keepFromEdge(position.value.x, w, lastWindowSize.value.width, currentWidth, anchor.right)
      const y = keepFromEdge(position.value.y, h, lastWindowSize.value.height, currentHeight, anchor.bottom)

      // Ensure panel stays within bounds
      position.value = clampToViewport({ x, y }, panelSize.value)
      clampPill()

      lastWindowSize.value = { width: currentWidth, height: currentHeight }

      debouncedSave()
    }

    const toggleLayout = () => {
      isVertical.value = !isVertical.value
      savePreferences()
      nextTick(clampPanel)
    }

    return {
      panelRef,
      pillRef,
      pillButtonRef,
      minimizeRef,
      position,
      panelTop,
      pillStyle,
      isMinimized,
      isVertical,
      isDragging,
      pillDragging,
      counts,
      visibleQueues,
      totalOpen,
      tipPlacement,
      panelOnTopBar,
      pillOnTopBar,
      shouldShowPanel,
      queueLabel,
      isCurrent,
      startPanelDrag,
      resetPanelPosition,
      onGripKeydown,
      startPillDrag,
      onPillClick,
      onPillKeydown,
      minimize,
      toggleLayout
    }
  }
}
</script>
